import {
    Box, Typography, IconButton, FormControl, Select, MenuItem,
    InputLabel, Switch, FormControlLabel, Chip, Tooltip,
} from '@mui/material';
import { useState, useCallback } from 'react';
import SettingsIcon from '@mui/icons-material/Settings';
import RefreshIcon from '@mui/icons-material/Refresh';
import {
    setActiveWeights, clearActiveWeights, hasActiveOverride,
} from '../../../gibbs/results';
import { defaultWeights } from '../../../gibbs/results';

/**
 * RankerWeightsControl — collapsible inline form in the ResultsPane
 * header.
 *
 * Theme `gibbs-ranker-weights-ui` (2026-05-15). Lifts the ranker-weights
 * freeze from the results-interpretation-layer MVP so users can:
 *   - pick a different primary score (e.g., dockingScore for Vina-only
 *     workspaces)
 *   - turn off the flag-count tiebreak when toxicophore alerts aren't
 *     relevant to the current campaign
 *
 * Workspace-scoped via `gibbs/results/rankerWeightsState.js` —
 * changing weights on workspace A doesn't affect B.
 *
 * @param {Object} props
 * @param {object|null} props.workspace - BMaps workspace
 *   (App.Workspace; nullable for graceful render before bootstrap)
 * @param {import('../../../gibbs/results').RankerWeights} props.activeWeights
 * @param {() => void} props.onWeightsChange - Called after a successful
 *   save so the parent can re-rank.
 */
export default function RankerWeightsControl({
    workspace, activeWeights, onWeightsChange,
}) {
    const [expanded, setExpanded] = useState(false);
    const [busy, setBusy] = useState(false);
    const overrideActive = hasActiveOverride(workspace);

    const setPrimary = useCallback(async (newPrimary) => {
        setBusy(true);
        try {
            // Place `newPrimary` first in the preference list, with the
            // remaining default entries trailing as fallback. Matches
            // the original semantics of compoundRanker's
            // `primaryScorePreference: first-non-null-wins`.
            const tail = defaultWeights.primaryScorePreference
                .filter((s) => s !== newPrimary);
            await setActiveWeights(workspace, {
                primaryScorePreference: [newPrimary, ...tail],
                tiebreakByFlagCount: activeWeights.tiebreakByFlagCount,
            });
            if (onWeightsChange) onWeightsChange();
        } finally {
            setBusy(false);
        }
    }, [workspace, activeWeights, onWeightsChange]);

    const toggleTiebreak = useCallback(async () => {
        setBusy(true);
        try {
            await setActiveWeights(workspace, {
                primaryScorePreference: activeWeights.primaryScorePreference,
                tiebreakByFlagCount: !activeWeights.tiebreakByFlagCount,
            });
            if (onWeightsChange) onWeightsChange();
        } finally {
            setBusy(false);
        }
    }, [workspace, activeWeights, onWeightsChange]);

    const resetToDefault = useCallback(async () => {
        setBusy(true);
        try {
            await clearActiveWeights(workspace);
            if (onWeightsChange) onWeightsChange();
        } finally {
            setBusy(false);
        }
    }, [workspace, onWeightsChange]);

    const activePrimary = activeWeights.primaryScorePreference[0];

    return (
        <Box sx={{ px: 1, pb: 0.5 }}>
            <Box sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 0.5,
            }}>
                <Tooltip title="Adjust ranker weights for this workspace">
                    <IconButton
                        size="small"
                        onClick={() => setExpanded((v) => !v)}
                        aria-label="Ranker settings"
                        sx={{ p: 0.25 }}
                    >
                        <SettingsIcon sx={{ fontSize: 14 }} />
                    </IconButton>
                </Tooltip>
                <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ flex: 1 }}
                >
                    {overrideActive
                        ? `Ranked by ${activePrimary}`
                        : 'Ranked by Auto (gnina → docking → total)'}
                    {!activeWeights.tiebreakByFlagCount && (
                        <Chip
                            size="small"
                            label="no flag tiebreak"
                            sx={{ ml: 0.5, height: 16, fontSize: '0.7em' }}
                        />
                    )}
                </Typography>
                {overrideActive && (
                    <Tooltip title="Reset to default weights">
                        <IconButton
                            size="small"
                            onClick={resetToDefault}
                            disabled={busy}
                            aria-label="Reset ranker weights to default"
                            sx={{ p: 0.25 }}
                        >
                            <RefreshIcon sx={{ fontSize: 14 }} />
                        </IconButton>
                    </Tooltip>
                )}
            </Box>
            {expanded && (
                <Box sx={{
                    mt: 0.5,
                    p: 1,
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: 1,
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 0.5,
                }}>
                    <FormControl size="small" disabled={busy}>
                        <InputLabel id="ranker-primary-label" sx={{ fontSize: '0.8em' }}>
                            Primary score
                        </InputLabel>
                        <Select
                            labelId="ranker-primary-label"
                            value={activePrimary}
                            label="Primary score"
                            onChange={(e) => setPrimary(e.target.value)}
                            sx={{ fontSize: '0.85em' }}
                        >
                            <MenuItem value="gninaScore">gninaScore</MenuItem>
                            <MenuItem value="dockingScore">dockingScore</MenuItem>
                            <MenuItem value="total">total</MenuItem>
                        </Select>
                    </FormControl>
                    <FormControlLabel
                        control={
                            <Switch
                                size="small"
                                checked={activeWeights.tiebreakByFlagCount}
                                onChange={toggleTiebreak}
                                disabled={busy}
                            />
                        }
                        label={(
                            <Typography variant="caption">
                                Tiebreak by flag count
                            </Typography>
                        )}
                    />
                </Box>
            )}
        </Box>
    );
}
